import OfflineLogger from '@/utils/offlineLogger';
import logger from '@/utils/logger';
import { clearAllTables } from '@/db/offlineDb';
import { imageCache } from '@/services/imageCache.service';
import { enhancedImageCacheService } from '@/services/offline/image-cache-enhanced.service';
import { initialSyncService } from '@/services/sync';
import { syncValidationService } from '@/services/sync/SyncValidationService';
import { productRepository } from '@/repositories/product.repository';
import type { InitialSyncStatus } from '@/services/sync/InitialSyncService';

export interface ResetLocalCacheOptions {
  userId?: string | null;
  isOnline: boolean;
  loggerContext?: string;
  onCacheCleared?: () => void;
  onBeforeDataReload?: () => void;
  triggerType?: SyncResetTriggerType;
}

export interface ResetLocalCacheResult {
  reloadedData: boolean;
  preloadedImages: number;
  initialSyncStatus: InitialSyncStatus | null;
  initialSyncError?: string;
}

const DEFAULT_LOGGER_CONTEXT = 'ResetLocalCache';

export type SyncResetTriggerType = 'remote-command' | 'manual' | 'system';

export interface SyncResetEventDetail {
  userId: string | null;
  loggerContext: string;
  triggerType: SyncResetTriggerType;
  isOnline: boolean;
}

export interface SyncResetProgressDetail extends SyncResetEventDetail {
  stage:
    | 'cache-clear-start'
    | 'cache-cleared'
    | 'initial-sync-start'
    | 'initial-sync-success'
    | 'initial-sync-skipped'
    | 'initial-sync-error';
  initialSyncStatus?: InitialSyncStatus | null;
  initialSyncError?: string;
}

export interface SyncResetCompletedDetail extends SyncResetEventDetail {
  reloadedData: boolean;
  preloadedImages: number;
  initialSyncStatus: InitialSyncStatus | null;
  initialSyncError?: string;
}

type SyncResetEventMap = {
  syncResetStarted: SyncResetEventDetail;
  syncResetProgress: SyncResetProgressDetail;
  syncResetCompleted: SyncResetCompletedDetail;
};

function dispatchSyncResetEvent<T extends keyof SyncResetEventMap>(
  type: T,
  detail: SyncResetEventMap[T],
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(type, { detail }));
}

function dispatchPendingOperationsUpdate(pendingOperations: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  const detail = { pendingOperations };
  window.dispatchEvent(new CustomEvent('syncStatusChanged', { detail }));
  window.dispatchEvent(new CustomEvent('syncCompleted', { detail }));
}

export async function resetLocalCache({
  userId,
  isOnline,
  loggerContext = DEFAULT_LOGGER_CONTEXT,
  onCacheCleared,
  onBeforeDataReload,
  triggerType = 'manual',
}: ResetLocalCacheOptions): Promise<ResetLocalCacheResult> {
  const baseDetail: SyncResetEventDetail = {
    userId: userId ?? null,
    loggerContext,
    triggerType,
    isOnline,
  };

  dispatchSyncResetEvent('syncResetStarted', baseDetail);
  dispatchSyncResetEvent('syncResetProgress', {
    ...baseDetail,
    stage: 'cache-clear-start',
  });

  await clearAllTables();

  if (userId) {
    logger.log(`[${loggerContext}] Avvio validazione completa post-reset`, {
      userId: userId.substring(0, 8)
    });
    await syncValidationService.performComprehensiveValidation(userId, true);
  } else {
    OfflineLogger.warn(
      `${loggerContext}.resetLocalCache`,
      'Missing user ID during validation'
    );
  }

  await imageCache.clearPersistentCache();
  await enhancedImageCacheService.clearCache();

  onCacheCleared?.();

  dispatchPendingOperationsUpdate(0);
  dispatchSyncResetEvent('syncResetProgress', {
    ...baseDetail,
    stage: 'cache-cleared',
  });

  let reloadedData = false;
  let preloadedImages = 0;
  let initialSyncStatus: InitialSyncStatus | null = null;
  let initialSyncError: string | undefined;

  if (isOnline) {
    onBeforeDataReload?.();
    dispatchSyncResetEvent('syncResetProgress', {
      ...baseDetail,
      stage: 'initial-sync-start',
    });

    logger.log(`[${loggerContext}] Avvio inizializzazione dati post-reset`);

    if (!userId) {
      initialSyncError = 'User ID non disponibile per la sincronizzazione iniziale';
      OfflineLogger.warn(
        `${loggerContext}.resetLocalCache`,
        'Missing user ID during initial sync'
      );
    } else {
      try {
        initialSyncStatus = await initialSyncService.performInitialSync(userId, true);

        if (initialSyncStatus.isComplete) {
          const products = await productRepository.getAll();
          const valid = products.filter(
            (p) =>
              p.photo &&
              !p.photo.startsWith('data:') &&
              !p.photo.startsWith('blob:') &&
              p.photo.startsWith('http')
          );

          if (valid.length > 0) {
            await enhancedImageCacheService.preloadImages(
              valid.map((p) => ({ id: p.id, photo: p.photo! }))
            );
            preloadedImages = valid.length;
          }

          reloadedData = true;
          dispatchSyncResetEvent('syncResetProgress', {
            ...baseDetail,
            stage: 'initial-sync-success',
            initialSyncStatus,
          });
        } else {
          initialSyncError =
            'Sincronizzazione iniziale incompleta: alcune entità non sono state aggiornate';
          OfflineLogger.warn(
            `${loggerContext}.resetLocalCache`,
            'Initial sync completed without reaching a valid state',
            {
              initialSyncStatus
            }
          );
          dispatchSyncResetEvent('syncResetProgress', {
            ...baseDetail,
            stage: 'initial-sync-skipped',
            initialSyncStatus,
            initialSyncError,
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error during initial sync';
        initialSyncError = message;
        OfflineLogger.error(`${loggerContext}.resetLocalCache`, error);
        dispatchSyncResetEvent('syncResetProgress', {
          ...baseDetail,
          stage: 'initial-sync-error',
          initialSyncStatus,
          initialSyncError,
        });
      }
    }
  } else {
    logger.info(
      `[${loggerContext}] Dispositivo offline: salta il ricaricamento dei dati`
    );
    dispatchSyncResetEvent('syncResetProgress', {
      ...baseDetail,
      stage: 'initial-sync-skipped',
      initialSyncStatus,
      initialSyncError,
    });
  }

  const result: ResetLocalCacheResult = { reloadedData, preloadedImages, initialSyncStatus, initialSyncError };

  dispatchSyncResetEvent('syncResetCompleted', {
    ...baseDetail,
    reloadedData,
    preloadedImages,
    initialSyncStatus,
    initialSyncError,
  });

  return result;
}
